import { useEffect, useRef, useState } from 'react';
import type { ToolId } from 'config/toolDefinitionsConfig';
import { hasManualSegmentationSourceVolume } from '../../rendering/Panels/Renderers/CornerstoneManager';
import { discardSegmentManager } from '../../segmentManager';

export function useModificationModeLifecycle({
  enabled,
  editMode,
  selectedSeriesId,
  lowQuality,
  manualMode,
  handleManualEnter,
  handleManualExit,
  setDirty,
}: {
  enabled: boolean;
  editMode: unknown;
  selectedSeriesId?: string | null;
  lowQuality: boolean;
  manualMode: boolean;
  handleManualEnter: (toolId: ToolId) => void;
  handleManualExit: () => void;
  setDirty:(value:boolean)=>void;
}) {
  const previousEditModeRef = useRef<typeof editMode>(null);
  const pendingBrushEnterRef = useRef(false);
  const [manualEnterRetryTick, setManualEnterRetryTick] = useState(0);

  const handleManualExitRef = useRef(handleManualExit);

  useEffect(() => {
    handleManualExitRef.current = handleManualExit;
  }, [handleManualExit]);

  useEffect(() => {
    if (enabled || !manualMode) return;
    pendingBrushEnterRef.current = false;
    discardSegmentManager(setDirty);
    handleManualExitRef.current();
  }, [enabled, manualMode, setDirty]);

  useEffect(() => {
    const previousEditMode = previousEditModeRef.current;
    const currentEditMode = editMode;
    previousEditModeRef.current = currentEditMode;

    if (!enabled || currentEditMode === null) {
      pendingBrushEnterRef.current = false;
      return;
    }

    if (previousEditMode !== currentEditMode) {
      discardSegmentManager(setDirty);
      handleManualExitRef.current();
    }

    pendingBrushEnterRef.current = true;
  }, [editMode, enabled, setDirty]);

  useEffect(() => {
    if (!enabled) return;
    if (!editMode || manualMode) return;
    pendingBrushEnterRef.current = true;
    setManualEnterRetryTick((prev) => prev + 1);
  }, [editMode, enabled, manualMode]);

  useEffect(() => {
    if (!enabled) return;
    if (manualMode) return;
    if (!pendingBrushEnterRef.current) return;
    if (!selectedSeriesId || !editMode) return;
    if (!hasManualSegmentationSourceVolume(selectedSeriesId, lowQuality)) {
      const retryTimer = window.setTimeout(() => {
        setManualEnterRetryTick((prev) => prev + 1);
      }, 120);
      return () => window.clearTimeout(retryTimer);
    }
    pendingBrushEnterRef.current = false;

    handleManualEnter('Brush');
  }, [
    editMode,
    enabled,
    handleManualEnter,
    manualEnterRetryTick,
    manualMode,
    lowQuality,
    selectedSeriesId,
  ]);
}
